/** Timer functions; hosts pass their window's so tests and zones can intercept them. */
export interface HsRefreshTimers {
  setTimeout(callback: () => void, delayMs: number): number;
  clearTimeout(handle: number): void;
}

/**
 * Debounces Frustum refreshes. One refresh runs at a time; a refresh asked for while one runs, or while
 * the camera moves, is remembered as pending and runs once the host calls `finish` or navigation ends.
 * Gating that depends on the host (scene loading, frozen view) stays with the host.
 */
export class HsFrustumRefreshScheduler {
  public timer: number | null = null;
  public pending: boolean = false;
  public inProgress: boolean = false;

  public constructor(private readonly timers: HsRefreshTimers) {}

  /**
   * Runs `refresh` after `delayMs`, replacing an earlier scheduled one. While `isNavigating()` is true or
   * a refresh runs, it only marks the refresh as pending.
   */
  public schedule(delayMs: number, isNavigating: () => boolean, refresh: () => void): void {
    this.cancelTimer();
    if (isNavigating() || this.inProgress) {
      this.pending = true;
      return;
    }
    this.timer = this.timers.setTimeout(() => {
      this.timer = null;
      if (isNavigating()) {
        this.pending = true;
        return;
      }
      refresh();
    }, Math.max(0, delayMs));
  }

  public cancelTimer(): void {
    if (this.timer !== null) {
      this.timers.clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /** Marks a refresh as started; returns whether one was pending, which the start consumes. */
  public begin(): boolean {
    const consumedPending: boolean = this.pending;
    this.inProgress = true;
    this.pending = false;
    return consumedPending;
  }

  /** Marks the running refresh as done; returns whether another was asked for meanwhile. */
  public finish(): boolean {
    const hadPending: boolean = this.pending;
    this.pending = false;
    this.inProgress = false;
    return hadPending;
  }

  /** Drops the scheduled and pending refreshes, for example when the scene closes. */
  public reset(): void {
    this.cancelTimer();
    this.pending = false;
    this.inProgress = false;
  }
}
